import {
  Sun,
  Sparkles,
  Layers,
  Calendar,
  CheckCircle2,
  StickyNote,
  Gift,
  TvMinimal,
  Bell,
  Keyboard,
  FlaskConical,
  CircleUser,
  Wallet,
  RefreshCcw,
  PlugZap,
  ShieldCheck,
  Compass,
  MessageSquareText,
  ClipboardList,
  FileText,
  Info,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { WorkModeIcon } from '@/src/renderer/components/icons/WorkModeIcon'
import { AppExperienceIcon } from '@/src/renderer/components/icons/AppExperienceIcon'
import { AccountDataIcon } from '@/src/renderer/components/icons/AccountDataIcon'
import { HelpAboutIcon } from '@/src/renderer/components/icons/HelpAboutIcon'

/**
 * Icon-component contract — broad enough to accept both `lucide-react`
 * icons (which take `size`/`strokeWidth`/`className`) and our own branded
 * SVG components.
 */
export type SettingsIcon = ComponentType<{
  size?: number | string
  strokeWidth?: number | string
  className?: string
}>

export interface SubNavEntry {
  /** Stable id, used in localStorage and the navigation hook. */
  id: string
  /** i18n key under `settings.subNav.*`. */
  i18nKey: string
  icon: SettingsIcon
}

export interface TopNavEntry {
  id: string
  i18nKey: string
  icon: SettingsIcon
  /** SubNav items rendered in the left sidebar when this TopNav is active. */
  subNav: ReadonlyArray<SubNavEntry>
}

/**
 * Source of truth for the Settings navigation tree. Order here is the order
 * shown in the UI. Adding a new SubNav: append an entry and ship its
 * i18n key under `settings.subNav.*` in both locales.
 *
 * Uses `as const satisfies` so the literal `id` strings survive — that
 * lets {@link TopNavId}, {@link SubNavId} and {@link SlotId} infer as
 * tight unions instead of plain `string`.
 */
export const SETTINGS_TREE = [
  {
    id: 'work-mode',
    i18nKey: 'settings.topNav.workMode',
    icon: WorkModeIcon,
    subNav: [
      { id: 'happy-mode', i18nKey: 'settings.subNav.happyMode', icon: Sparkles },
      { id: 'lifespaces', i18nKey: 'settings.subNav.lifespaces', icon: Layers },
      { id: 'day-workflow', i18nKey: 'settings.subNav.dayWorkflow', icon: Calendar },
      { id: 'task-behavior', i18nKey: 'settings.subNav.taskBehavior', icon: CheckCircle2 },
      { id: 'quick-notes', i18nKey: 'settings.subNav.quickNotes', icon: StickyNote },
      { id: 'rewards', i18nKey: 'settings.subNav.rewards', icon: Gift },
    ],
  },
  {
    id: 'app-experience',
    i18nKey: 'settings.topNav.appExperience',
    icon: AppExperienceIcon,
    subNav: [
      { id: 'appearance', i18nKey: 'settings.subNav.appearance', icon: Sun },
      { id: 'app-behavior', i18nKey: 'settings.subNav.appBehavior', icon: TvMinimal },
      { id: 'notifications', i18nKey: 'settings.subNav.notifications', icon: Bell },
      { id: 'shortcuts', i18nKey: 'settings.subNav.shortcuts', icon: Keyboard },
      { id: 'lab', i18nKey: 'settings.subNav.lab', icon: FlaskConical },
    ],
  },
  {
    id: 'account-data',
    i18nKey: 'settings.topNav.accountData',
    icon: AccountDataIcon,
    subNav: [
      { id: 'account-profile', i18nKey: 'settings.subNav.accountProfile', icon: CircleUser },
      { id: 'subscription', i18nKey: 'settings.subNav.subscription', icon: Wallet },
      { id: 'sync', i18nKey: 'settings.subNav.sync', icon: RefreshCcw },
      { id: 'integrations', i18nKey: 'settings.subNav.integrations', icon: PlugZap },
      { id: 'data-privacy', i18nKey: 'settings.subNav.dataPrivacy', icon: ShieldCheck },
    ],
  },
  {
    id: 'help-about',
    i18nKey: 'settings.topNav.helpAbout',
    icon: HelpAboutIcon,
    subNav: [
      { id: 'first-steps', i18nKey: 'settings.subNav.firstSteps', icon: Compass },
      { id: 'feedback', i18nKey: 'settings.subNav.feedback', icon: MessageSquareText },
      { id: 'changelog', i18nKey: 'settings.subNav.changelog', icon: ClipboardList },
      { id: 'legal', i18nKey: 'settings.subNav.legal', icon: FileText },
      { id: 'about', i18nKey: 'settings.subNav.about', icon: Info },
    ],
  },
] as const satisfies ReadonlyArray<TopNavEntry>

// ---------------------------------------------------------------------------
// Derived types — kept generic over `typeof SETTINGS_TREE` so adding a new
// TopNav or SubNav to the literal above automatically widens the unions.
// ---------------------------------------------------------------------------

/** Union of all TopNav ids that exist in {@link SETTINGS_TREE}. */
export type TopNavId = (typeof SETTINGS_TREE)[number]['id']

/** Union of every SubNav id across all TopNavs. */
export type SubNavId = (typeof SETTINGS_TREE)[number]['subNav'][number]['id']

/**
 * Template-literal of every theoretically valid `<topNav>/<subNav>` pair.
 * Looser than a "real combinations only" mapped type, but trivial to
 * understand and good enough — every real slot id matches this pattern,
 * and impossible pairs (`'work-mode/appearance'`) are still rejected at
 * runtime by {@link findSubNav}.
 */
export type SlotId = `${TopNavId}/${SubNavId}`

// ---------------------------------------------------------------------------
// Runtime helpers — accept `string` at the boundary (localStorage etc.) and
// narrow into the tight union types via type guards.
// ---------------------------------------------------------------------------

const TOP_NAV_IDS: ReadonlySet<TopNavId> = new Set(
  SETTINGS_TREE.map((t) => t.id),
)

const SUB_NAV_IDS: ReadonlySet<SubNavId> = new Set(
  SETTINGS_TREE.flatMap((t) => t.subNav.map((s) => s.id)),
)

export function isTopNavId(value: unknown): value is TopNavId {
  return typeof value === 'string' && TOP_NAV_IDS.has(value as TopNavId)
}

export function isSubNavId(value: unknown): value is SubNavId {
  return typeof value === 'string' && SUB_NAV_IDS.has(value as SubNavId)
}

/** Build the canonical `<topNav>/<subNav>` slot id. */
export function slotId(topNavId: TopNavId, subNavId: SubNavId): SlotId {
  return `${topNavId}/${subNavId}` as SlotId
}

/** Default landing — matches the Figma "App & Bedienung > Erscheinungsbild". */
export const DEFAULT_NAV: { topNavId: TopNavId; subNavId: SubNavId } = {
  topNavId: 'app-experience',
  subNavId: 'appearance',
}

/**
 * Look up a TopNav entry by id. Returns undefined if not found — callers
 * that need a fallback should default to {@link SETTINGS_TREE}[0].
 */
export function findTopNav(id: string): (typeof SETTINGS_TREE)[number] | undefined {
  return SETTINGS_TREE.find((t) => t.id === id)
}

/** Look up a SubNav entry inside a given TopNav. */
export function findSubNav(topNavId: string, subNavId: string) {
  return findTopNav(topNavId)?.subNav.find((s) => s.id === subNavId)
}
